// 沙盘界面的小部件:线条图标、地图上的浮动说明、快讯弹条。和 sandbox.ts 分开,那边只管数据和面板。
// 图标一律 24 格、1.8 粗的线,和主游戏底部导航同一种笔触(11-美术.md)。
import type { Plot } from './api.ts';
import { hash } from './art.ts';
import { esc } from './sbchart.ts';

const PATHS: Record<string, string> = {
  engine: '<rect x="6" y="6" width="12" height="12" rx="2"/><path d="M9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M2 15h4M18 9h4M18 15h4"/><path d="M10 10h4v4h-4z"/>',
  overview: '<rect x="3.5" y="3.5" width="7" height="7" rx="1.5"/><rect x="13.5" y="3.5" width="7" height="7" rx="1.5"/><rect x="3.5" y="13.5" width="7" height="7" rx="1.5"/><rect x="13.5" y="13.5" width="7" height="7" rx="1.5"/>',
  people: '<circle cx="9" cy="8" r="3.2"/><path d="M3.5 20c0-3.3 2.5-5.5 5.5-5.5s5.5 2.2 5.5 5.5"/><circle cx="17" cy="9" r="2.5"/><path d="M15.8 14.6c.4-.1.8-.1 1.2-.1 2.6 0 4.5 1.9 4.5 4.8"/>',
  medical: '<path d="M8 3h8v5h5v8h-5v5H8v-5H3V8h5V3Z"/>',
  education: '<path d="m2.5 8 9.5-5 9.5 5-9.5 5-9.5-5ZM6 10v7c3.5 3 8.5 3 12 0v-7M21.5 8v8"/>',
  market: '<path d="M3 9l2-5h14l2 5M3 9h18v2a3 3 0 0 1-6 0 3 3 0 0 1-6 0 3 3 0 0 1-6 0V9ZM5 13v8h14v-8"/>',
  estate: '<path d="M3 21h18M5 21V9l6-4v16M11 21V3l8 4v14"/><path d="M14 10h2M14 13h2M14 16h2M7.5 12h1M7.5 15h1"/>',
  firms: '<rect x="3" y="7" width="18" height="13" rx="2"/><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M3 12.5h18"/>',
  finance: '<path d="M3 9.5 12 4l9 5.5M4 20h16M5.5 10v8M10 10v8M14 10v8M18.5 10v8"/>',
  govt: '<path d="M4 20h16M6 20v-7M10 20v-7M14 20v-7M18 20v-7M4.5 13h15M7 13a5 5 0 0 1 10 0M12 8V4M12 4h3.5v2H12"/>',
  gov: '<ellipse cx="12" cy="6" rx="7" ry="2.5"/><path d="M5 6v4c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5V6M5 10v4c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5v-4M5 14v4c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5v-4"/>',
  regions: '<circle cx="6" cy="18" r="2.2"/><circle cx="18" cy="6" r="2.2"/><path d="M8.2 18h6.3a3.5 3.5 0 0 0 0-7h-5a3.5 3.5 0 0 1 0-7h6.3"/>',
  news: '<path d="M4 5h13v14a1 1 0 0 0 1 1H6a2 2 0 0 1-2-2V5Z"/><path d="M17 9h3v9a2 2 0 0 1-2 2M7.5 9h6M7.5 12.5h6M7.5 16h4"/>',
  maps: '<path d="M12 3 21 8l-9 5-9-5 9-5Z"/><path d="m3 12 9 5 9-5M3 16l9 5 9-5"/>',
  // 股市:两根蜡烛和一条走势
  stocks: '<path d="M3.5 20.5h17"/><path d="M7 4v3M7 13v3"/><rect x="5" y="7" width="4" height="6" rx=".8"/><path d="M15 8v2.5M15 16.5V19"/><rect x="13" y="10.5" width="4" height="6" rx=".8"/>',
  pause: '<rect x="6.5" y="5" width="3.6" height="14" rx="1" fill="currentColor" stroke="none"/><rect x="13.9" y="5" width="3.6" height="14" rx="1" fill="currentColor" stroke="none"/>',
  day: '<circle cx="12" cy="12" r="4"/><path d="M12 2.5v2M12 19.5v2M4.6 4.6l1.4 1.4M18 18l1.4 1.4M2.5 12h2M19.5 12h2M4.6 19.4 6 18M18 6l1.4-1.4"/>',
  dusk: '<path d="M3 17h18M6.5 17a5.5 5.5 0 0 1 11 0M12 5.5v2M5.6 8.6 7 10M18.4 8.6 17 10M6 20.5h12"/>',
  night: '<path d="M19.5 14.5A8 8 0 1 1 9.5 4.5a6.5 6.5 0 0 0 10 10Z"/>',
  // 天色自动:半明半暗的圆
  auto: '<circle cx="12" cy="12" r="8"/><path d="M12 4a8 8 0 0 1 0 16Z" fill="currentColor" stroke="none"/>',
  dawn: '<path d="M3 17h18M6.5 17a5.5 5.5 0 0 1 11 0M12 4v5M9.6 6.4 12 4l2.4 2.4M6 20.5h12"/>',
  reseed: '<path d="M20 11a8 8 0 0 0-14.3-4.9L4 8M4 4v4h4M4 13a8 8 0 0 0 14.3 4.9L20 16M20 20v-4h-4"/>',
  close: '<path d="M6.5 6.5l11 11M17.5 6.5l-11 11"/>',
  locate: '<circle cx="12" cy="12" r="6.5"/><path d="M12 2.5v4M12 17.5v4M2.5 12h4M17.5 12h4"/><circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none"/>',
  back: '<path d="M14.5 5.5 8 12l6.5 6.5"/>',
  help: '<circle cx="12" cy="12" r="9"/><path d="M9.6 9.3a2.5 2.5 0 0 1 4.9.8c0 1.7-2.5 2.1-2.5 3.8M12 17.1v.2"/>',
  star: '<path d="m12 3.6 2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.2-4.1 5.8-.8Z"/>',
  follow: '<path d="m12 3.6 2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.2-4.1 5.8-.8Z"/>',
  // 社会:一颗心
  society: '<path d="M12 19.5s-7.5-4.5-7.5-10A4.2 4.2 0 0 1 12 7a4.2 4.2 0 0 1 7.5 2.5c0 5.5-7.5 10-7.5 10Z"/>',
  // 司法:天平
  justice: '<path d="M12 4.5V20M8 20h8M4.5 7h15M12 4.5v-.5M4.5 7 2.5 13M4.5 7l2 6M19.5 7l-2 6M19.5 7l2 6"/><path d="M2 13h5a2.5 2.5 0 0 1-5 0ZM17 13h5a2.5 2.5 0 0 1-5 0Z"/>',
  // 建设:安全帽
  build: '<path d="M3.5 17.5h17M5 17.5v-2.2a7 7 0 0 1 14 0v2.2M10 8.6V6.5h4v2.1M9.5 11.5V9.3M14.5 11.5V9.3"/>',
  // 塔吊:塔身、吊臂、平衡臂、吊钩
  crane: '<path d="M8 21V6M5 21h9M8 6h12M8 6l-4 3h4M8 6l3-3 9 3M17.5 6v5"/><rect x="16" y="11" width="3" height="2.5" rx=".4"/>',
};

/** 线条图标(颜色跟着字的颜色走)。 */
export function icon(name: string, size = 18): string {
  return `<svg class="ic" viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${PATHS[name] ?? ''}</svg>`;
}

// ---------- 头像、公司徽标 ----------

const SKIN = ['#f1d3b3', '#e8c29f', '#d9a982', '#c68f68', '#a87252'];
const HAIR = ['#2b2320', '#3a2a22', '#4a3526', '#1f1c1a', '#5a4030', '#6b4a2e'];
const BG = ['#e9dcc6', '#dfe6e0', '#e6dbe4', '#dde3ea', '#efe0cf', '#e2e5d3'];
const SHIRT = ['#3f6e8c', '#4f8a5b', '#8a5d6b', '#b5553d', '#7a5c8e', '#5b7fa3', '#9c7c3a', '#2f5a52'];
const CRAFT = /木工|钢筋工|混凝土工|瓦工|水电工|装饰工|防水工|建筑/;

/**
 * 一个人的头像:按编号定长相(肤色、发型、眼镜),按年纪白头发,按干什么定穿什么
 * (工地上的戴安全帽穿反光背心、医生白大褂、警察制服、总经理西装),嘴角跟着心情。同一个人每次画都一样。
 */
export function avatar(id: number, age: number, role: string, happy = 0.6, size = 32): string {
  const r = (k: number) => hash(id * 131 + k * 7919);
  const pick = <T,>(xs: T[], k: number) => xs[Math.floor(r(k) * xs.length) % xs.length];
  const kid = age < 16;
  const skin = pick(SKIN, 1);
  const hair = age >= 72 ? '#dcd7cf' : age >= 58 ? '#9d978f' : pick(HAIR, 2);
  let shirt = pick(SHIRT, 4);
  let hat = '';
  let extra = '';
  if (!kid && CRAFT.test(role)) {
    shirt = '#d08a3a';
    extra = '<path d="M8.5 27.5h15" stroke="#f4f1ea" stroke-width="1.3"/>';
    hat = '<path d="M9 12.4Q9.4 5.2 16 5.2T23 12.4Z" fill="#e8b030"/><rect x="7.6" y="11.6" width="16.8" height="1.8" rx=".9" fill="#c99020"/>';
  } else if (/医生|医院|诊所/.test(role)) {
    shirt = '#f4f1ea';
    extra = '<path d="M13.6 23q-.8 3.6 1.6 5" stroke="#6d8a9c" stroke-width=".9" fill="none"/>';
  } else if (/警察|派出所/.test(role)) {
    shirt = '#2f3a46';
    hat = '<path d="M9.6 10.6Q10 6 16 6t6.4 4.6Z" fill="#2f3a46"/><rect x="9" y="10" width="14" height="1.8" rx=".9" fill="#1f262e"/><circle cx="16" cy="8.2" r="1" fill="#c9c3b8"/>';
  } else if (/总经理|老板|行长/.test(role)) {
    shirt = '#34322e';
    extra = '<path d="M13.2 22.8 16 27l2.8-4.2Z" fill="#f4f1ea"/>';
  } else if (/环卫/.test(role)) {
    shirt = '#d08a3a';
  } else if (/教师/.test(role)) {
    shirt = '#6d8f6a';
  } else if (/没活干/.test(role)) {
    shirt = '#a39d93';
  } else if (/退休/.test(role)) {
    shirt = '#9c8c74';
  }
  const style = kid ? 0 : Math.floor(r(3) * 5);
  const bald = age >= 58 && r(5) < 0.3;
  const back = style === 2 && !hat ? `<path d="M8.6 15Q8 5.6 16 5.6T23.4 15L24 24H8Z" fill="${hair}"/>` : '';
  const fronts = [
    'M9.8 13.2Q9.4 6.6 16 6.6t6.2 6.6Q20.6 9.6 16 9.8t-6.2 3.4Z',
    'M9.8 14Q9 6.4 16 6.4t6.3 7.2q-.9-3.2-2.8-3.8-4.5 2.2-8.7 1-.8 1.2-1 3.2Z',
    'M9.8 13.2Q9.4 6.6 16 6.6t6.2 6.6Q20.6 9.6 16 9.8t-6.2 3.4Z',
    'M9.8 13.2Q9.4 6.6 16 6.6t6.2 6.6Q20.6 9.6 16 9.8t-6.2 3.4ZM16 3a2.6 2.6 0 1 1 0 5.2A2.6 2.6 0 0 1 16 3Z',
    'M10.2 12q.2-4.7 5.8-4.7t5.8 4.7Q19.4 9.6 16 9.6T10.2 12Z',
  ];
  const front = hat ? '' : bald ? `<path d="M9.9 14q-.1-2.5.9-3.5l.4 2.5ZM22.1 14q.1-2.5-.9-3.5l-.4 2.5Z" fill="${hair}"/>` : `<path d="${fronts[style]}" fill="${hair}"/>`;
  const cy = kid ? 15 : 13.5;
  const hr = kid ? 5.6 : 6.2;
  const ey = cy + 0.4;
  const mouth = happy > 0.62 ? `M13.6 ${cy + 3.7}q2.4 2 4.8 0` : happy < 0.35 ? `M13.8 ${cy + 5.1}q2.2-1.6 4.4 0` : `M14 ${cy + 4.4}h4`;
  const glasses = !kid && r(7) < 0.18 ? `<g stroke="#3a3530" stroke-width=".7" fill="none"><circle cx="13.7" cy="${ey}" r="1.9"/><circle cx="18.3" cy="${ey}" r="1.9"/><path d="M15.6 ${ey}h.8"/></g>` : '';
  return `<svg class="av" viewBox="0 0 32 32" width="${size}" height="${size}" aria-hidden="true"><rect width="32" height="32" fill="${pick(BG, 6)}"/>${back}` +
    `<path d="M4 32q.5-9.5 12-9.5T28 32Z" fill="${shirt}"/>${extra}<rect x="13.8" y="${cy + 4.5}" width="4.4" height="5" rx="1.5" fill="${skin}"/>` +
    `<circle cx="16" cy="${cy}" r="${hr}" fill="${skin}"/>${front}${hat}` +
    `<circle cx="13.7" cy="${ey}" r=".85" fill="#2b2320"/><circle cx="18.3" cy="${ey}" r=".85" fill="#2b2320"/>${glasses}` +
    `<path d="${mouth}" stroke="#6b3b2e" stroke-width=".9" fill="none" stroke-linecap="round"/></svg>`;
}

/** 各行各业的颜色(徽标、行业标签、图例共用)。 */
export const KIND_COLOR: Record<string, string> = {
  shop: '#4f8a5b', fun: '#8a5d6b', goods: '#9c7c3a', plant: '#8f8a82', builder: '#d08a3a', developer: '#3f6e8c', factory: '#5b7fa3',
  maker: '#6d8f6a', haul: '#2f5a52', service: '#7a5c8e', media: '#3d4a5c', fund: '#5f5a8a', insurer: '#4d6b7f', bank: '#34404d', public: '#b5553d', hotel: '#a0603a',
};

/** 公司的徽标:公司名的头一个字,底色是行业的颜色。 */
export function badge(name: string, kind: string, size = 28): string {
  const ch = [...String(name ?? '')][0] ?? '?';
  return `<span class="bdg" style="--c:${KIND_COLOR[kind] ?? '#8f8a82'};width:${size}px;height:${size}px;font-size:${Math.round(size * 0.52)}px">${esc(ch)}</span>`;
}

/** 行业标签:行业的颜色淡淡地铺底。 */
export function kindPill(kind: string, label: string): string {
  return `<span class="kp" style="--c:${KIND_COLOR[kind] ?? '#8f8a82'}">${esc(label)}</span>`;
}

// ---------- 地图上的浮动说明 ----------

const CLASS_NAME = ['荒地', '郊区', '近郊', '居民区', '市区', '市中心'];
const TYPE_NAME: Record<string, string> = {
  shophouse: '店屋', villa: '洋房', housing: '住宅', highrise: '高层', office: '写字楼', landmark: '地标',
  school: '学校', hospital: '医院', police: '派出所', cityhall: '市政大楼', tvtower: '电视塔', stadium: '体育场', theater: '大剧院', expo: '会展中心',
};
const STAGE_NAME: Record<string, string> = {
  prep: '等人接', piles: '打桩', caps: '承台', structure: '主体', finishing: '装修', acceptance: '验收', delivered: '', collapsed: '废墟',
};

/** 鼠标停在一块地上时的说明:这块地上是什么、谁的、盖到哪了;开着地图模式的话加上这一格的数。 */
export function plotTip(p: Plot & { class?: number }, mode?: { name: string; value: string; color: string | null }): string {
  const building = !!p.projectId && p.stage !== 'collapsed';
  const title = p.publicName ?? (p.stage === 'collapsed' ? '废墟' : building ? p.name || '一栋楼' : '空地');
  const bits: string[] = [];
  if (building) {
    const t = TYPE_NAME[p.type ?? ''] ?? '';
    const stage = STAGE_NAME[p.stage ?? ''] ?? '';
    const fl = p.target > p.floors ? `${p.floors}/${p.target} 层` : `${p.floors} 层`;
    bits.push([t, fl, stage].filter(Boolean).join(' · '));
  }
  const tags = [p.stalled ? '<span class="bad">停工</span>' : '', p.cracked ? '<span class="bad">有裂缝</span>' : ''].filter(Boolean).join(' ');
  const owner = !p.publicName && p.company ? `<div class="l">${esc(p.company)}</div>` : '';
  const where = `${p.class != null ? CLASS_NAME[p.class] ?? '' : ''} (${p.x}, ${p.y})`;
  const mv = mode ? `<div class="v">${mode.color ? `<i style="background:${mode.color}"></i>` : ''}${esc(mode.name)}<b>${esc(mode.value)}</b></div>` : '';
  return `<b>${esc(title)}</b>${bits.length ? `<div class="l">${esc(bits.join(''))}${tags ? ` ${tags}` : ''}</div>` : tags ? `<div class="l">${tags}</div>` : ''}${owner}<div class="l m">${esc(where)}</div>${mv}`;
}

/** 浮动说明摆在鼠标右下,靠边了翻到另一边。 */
export function placeTip(el: HTMLElement, x: number, y: number) {
  const w = el.offsetWidth, h = el.offsetHeight;
  const W = window.innerWidth, H = window.innerHeight;
  el.style.left = `${x + 16 + w > W - 8 ? x - w - 12 : x + 16}px`;
  el.style.top = `${y + 18 + h > H - 8 ? y - h - 10 : y + 18}px`;
}

// ---------- 快讯弹条 ----------

/** 新出的大事在屏幕下方弹一条,六秒后自己收掉;点一下打开相关的地、公司、人。同时最多三条。 */
export function toast(box: HTMLElement, html: string, go: string) {
  const el = document.createElement('div');
  el.className = 'sb-toast';
  if (go) el.dataset.go = go;
  el.innerHTML = html;
  box.appendChild(el);
  while (box.children.length > 3) box.firstElementChild!.remove();
  setTimeout(() => el.remove(), 6200);
}
